import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const waitlistSchema = z.object({
  companyOrOrganization: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  role: z.string().optional(),
  email: z.string().email("Invalid email address"),
  signupAs: z.array(z.enum(["My Self", "My Company"])).min(1, "Please select at least one option"),
  areaOfExpertise: z.string().optional(),
  industry: z.string().optional(),
  scheduleBriefing: z.boolean().default(false),
  authorizeCompany: z.boolean().default(false),
  includeNameWaitlist: z.boolean().default(false),
  inviteFriend: z.boolean().default(false),
  comments: z.string().optional(),
});

type WaitlistFormValues = z.infer<typeof waitlistSchema>;

const Waitlist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customExpertiseOptions, setCustomExpertiseOptions] = useState<string[]>([]);
  const [showCustomExpertise, setShowCustomExpertise] = useState(false);
  const [customExpertiseValue, setCustomExpertiseValue] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: {
      signupAs: [],
      scheduleBriefing: false,
      authorizeCompany: false,
      includeNameWaitlist: false,
      inviteFriend: false,
    },
  });

  const signupAs = watch("signupAs");

  // Load custom expertise options from database
  useEffect(() => {
    const loadCustomExpertise = async () => {
      const { data, error } = await supabase
        .from("waitlist")
        .select("area_of_expertise")
        .not("area_of_expertise", "is", null)
        .not("area_of_expertise", "in", '("construction","engineering","procurement","operations","management","other","")');
      
      if (!error && data) {
        const uniqueOptions = Array.from(new Set(data.map(d => d.area_of_expertise).filter(Boolean))) as string[];
        setCustomExpertiseOptions(uniqueOptions);
      }
    };
    
    loadCustomExpertise();
  }, []);

  const onSubmit = async (data: WaitlistFormValues) => {
    setIsSubmitting(true);

    try {
      // Use custom expertise value if "Other" was selected and custom value provided
      const finalExpertise = showCustomExpertise && customExpertiseValue.trim() 
        ? customExpertiseValue.trim() 
        : data.areaOfExpertise;

      const { error } = await supabase.from("waitlist").insert({
        company_or_organization: data.companyOrOrganization,
        first_name: data.firstName,
        last_name: data.lastName,
        role: data.role,
        email: data.email,
        signup_as: data.signupAs.join("; "),
        area_of_expertise: finalExpertise,
        industry: data.industry,
        schedule_briefing: data.scheduleBriefing,
        authorize_company: data.authorizeCompany,
        include_name_waitlist: data.includeNameWaitlist,
        invite_friend: data.inviteFriend,
        comments: data.comments,
        user_id: user?.id || null,
      });

      if (error) throw error;

      // Mark onboarding as completed
      if (user) {
        await supabase
          .from("profiles")
          .update({ has_completed_onboarding: true })
          .eq("id", user.id);
      }

      toast({
        title: "Joined Waitlist!",
        description: "Thank you for your interest. Let's create your profile.",
      });

      navigate("/intro");
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-2xl bg-background/95 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-3xl text-center">Join the Waitlist</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyOrOrganization">Company or Organization Name</Label>
              <Input
                id="companyOrOrganization"
                {...register("companyOrOrganization")}
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  {...register("firstName")}
                  className={errors.firstName ? "border-destructive" : ""}
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  {...register("lastName")}
                  className={errors.lastName ? "border-destructive" : ""}
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input id="role" {...register("role")} placeholder="Optional" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>I'm signing up on behalf of: *</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="myself"
                    checked={signupAs.includes("My Self")}
                    onCheckedChange={(checked) => {
                      const current = signupAs;
                      if (checked) {
                        setValue("signupAs", [...current, "My Self"]);
                      } else {
                        setValue("signupAs", current.filter((v) => v !== "My Self"));
                      }
                    }}
                  />
                  <Label htmlFor="myself" className="cursor-pointer">
                    My Self
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="company"
                    checked={signupAs.includes("My Company")}
                    onCheckedChange={(checked) => {
                      const current = signupAs;
                      if (checked) {
                        setValue("signupAs", [...current, "My Company"]);
                      } else {
                        setValue("signupAs", current.filter((v) => v !== "My Company"));
                      }
                    }}
                  />
                  <Label htmlFor="company" className="cursor-pointer">
                    My Company
                  </Label>
                </div>
              </div>
              {errors.signupAs && (
                <p className="text-sm text-destructive">{errors.signupAs.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="areaOfExpertise">Area of Expertise</Label>
              <Select
                onValueChange={(value) => {
                  setValue("areaOfExpertise", value);
                  setShowCustomExpertise(value === "other");
                  if (value !== "other") {
                    setCustomExpertiseValue("");
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select area of expertise" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="construction">Construction</SelectItem>
                  <SelectItem value="engineering">Engineering</SelectItem>
                  <SelectItem value="procurement">Procurement</SelectItem>
                  <SelectItem value="operations">Operations</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  {customExpertiseOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other (specify below)</SelectItem>
                </SelectContent>
              </Select>
              {showCustomExpertise && (
                <div className="mt-2">
                  <Input
                    placeholder="Please specify your area of expertise"
                    value={customExpertiseValue}
                    onChange={(e) => setCustomExpertiseValue(e.target.value)}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input id="industry" {...register("industry")} placeholder="Optional" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="scheduleBriefing"
                  onCheckedChange={(checked) =>
                    setValue("scheduleBriefing", checked as boolean)
                  }
                />
                <Label htmlFor="scheduleBriefing" className="cursor-pointer">
                  Schedule a briefing with iSPAI
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="authorizeCompany"
                  onCheckedChange={(checked) =>
                    setValue("authorizeCompany", checked as boolean)
                  }
                />
                <Label htmlFor="authorizeCompany" className="cursor-pointer">
                  Authorize my company name on iSPAI's waitlist
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeNameWaitlist"
                  onCheckedChange={(checked) =>
                    setValue("includeNameWaitlist", checked as boolean)
                  }
                />
                <Label htmlFor="includeNameWaitlist" className="cursor-pointer">
                  Include my name on iSPAI's waitlist
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="inviteFriend"
                  onCheckedChange={(checked) =>
                    setValue("inviteFriend", checked as boolean)
                  }
                />
                <Label htmlFor="inviteFriend" className="cursor-pointer">
                  Invite a friend or colleague to iSPAI
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                {...register("comments")}
                placeholder="Optional comments or questions"
                rows={4}
              />
            </div>

            <Button
              type="submit"
              variant="active"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Join Waitlist"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Waitlist;
